'use client';

import { useState, useRef, useEffect } from 'react';
import { useChat } from '@/lib/chat-context';
import { useAuth } from '@/lib/auth-context';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import MessageBubble from './message-bubble';
import TypingIndicator from './typing-indicator';
import { X, Send, Smile, Users, MessageCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import EmojiPicker from 'emoji-picker-react';

export default function GlobalChatPanel() {
    const { user } = useAuth();
    const {
        isChatOpen,
        toggleChat,
        channels,
        currentChannel,
        switchChannel,
        messages,
        sendMessage,
        typingUsers,
        setTyping,
        onlineUsers,
        unreadCounts,
    } = useChat();

    const [messageInput, setMessageInput] = useState('');
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, currentChannel]);

    const handleSendMessage = () => {
        if (!messageInput.trim() || !currentChannel) return;

        sendMessage(messageInput, currentChannel);
        setMessageInput('');
        setShowEmojiPicker(false);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setMessageInput(e.target.value);

        // Send typing indicator
        if (currentChannel) {
            setTyping(currentChannel);
        }
    };

    const handleEmojiClick = (emojiData: any) => {
        setMessageInput(prev => prev + emojiData.emoji);
    };

    if (!isChatOpen) return null;

    const currentMessages = currentChannel ? messages[currentChannel] || [] : [];
    const globalChannels = channels.filter(c => c.type === 'global');

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 md:hidden"
                onClick={toggleChat}
            />

            {/* Chat Panel */}
            <Card
                className={cn(
                    'fixed bottom-0 right-0 z-50 flex flex-col shadow-2xl transition-all duration-300',
                    'md:bottom-6 md:right-6',
                    'w-full h-[calc(100vh-4rem)] md:w-[380px] md:h-[600px]',
                    'md:rounded-lg rounded-t-lg'
                )}
            >
                {/* Header */}
                <CardHeader className="flex-shrink-0 border-b">
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Users className="w-5 h-5" />
                            Global Chat
                            <Badge variant="secondary" className="text-xs">
                                {onlineUsers.length} online
                            </Badge>
                        </CardTitle>
                        <Button variant="ghost" size="icon" onClick={toggleChat}>
                            <X className="w-5 h-5" />
                        </Button>
                    </div>

                    {/* Channel Tabs */}
                    <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
                        {globalChannels.map(channel => (
                            <Button
                                key={channel.id}
                                variant={currentChannel === channel.id ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => switchChannel(channel.id)}
                                className="relative flex-shrink-0"
                            >
                                {channel.name}
                                {unreadCounts[channel.id] > 0 && currentChannel !== channel.id && (
                                    <Badge className="ml-1.5 bg-red-500 hover:bg-red-500 min-w-[18px] h-4 px-1 text-[10px]">
                                        {unreadCounts[channel.id]}
                                    </Badge>
                                )}
                            </Button>
                        ))}
                    </div>
                </CardHeader>

                {/* Messages */}
                <CardContent className="flex-1 p-0 overflow-hidden">
                    <ScrollArea ref={scrollRef} className="h-full px-4 py-4">
                        {currentMessages.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
                                <MessageCircle className="w-12 h-12 mb-2 opacity-20" />
                                <p className="text-sm">No messages yet</p>
                                <p className="text-xs">Be the first to say something!</p>
                            </div>
                        ) : (
                            <div className="space-y-1">
                                {currentMessages.map(message => (
                                    <MessageBubble
                                        key={message.id}
                                        message={message}
                                        isOwn={message.userId === user?.id}
                                    />
                                ))}
                            </div>
                        )}

                        {/* Typing Indicator */}
                        <TypingIndicator typingUsers={typingUsers} currentChannelId={currentChannel} />
                    </ScrollArea>
                </CardContent>

                {/* Input */}
                <div className="flex-shrink-0 border-t p-4">
                    {/* Emoji Picker */}
                    {showEmojiPicker && (
                        <div className="absolute bottom-20 right-4 z-10">
                            <EmojiPicker onEmojiClick={handleEmojiClick} width={320} height={400} />
                        </div>
                    )}

                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                            className="flex-shrink-0"
                        >
                            <Smile className="w-4 h-4" />
                        </Button>
                        <Input
                            placeholder="Type a message..."
                            value={messageInput}
                            onChange={handleInputChange}
                            onKeyPress={handleKeyPress}
                            className="flex-1"
                        />
                        <Button
                            onClick={handleSendMessage}
                            disabled={!messageInput.trim()}
                            className="flex-shrink-0"
                        >
                            <Send className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            </Card>
        </>
    );
}
